import {TransferType, ZahavDayType} from '@sollapay/enums';
import {
  BUSINESS_TIME_ZONE,
  addDaysToIsoDate,
  getWallClockInTimeZone,
  wallClockToInstant
} from '@sollapay/utils';

import {getBankDayType} from './bank-calendar.rule';

import type {PayinTransferType} from '@sollapay/enums';
import type {PayAvailabilityDTO} from '@sollapay/types';

export const ZAHAV_OPEN_TIME = '07:45';
// BoI customer hours run to 18:45; the system itself closes at 18:30 — we take the stricter one.
export const ZAHAV_REGULAR_CLOSE_TIME = '18:30';
export const ZAHAV_SHORT_CLOSE_TIME = '14:00';

const OPEN_MINUTES = toMinutes(ZAHAV_OPEN_TIME);
const CLOSE_MINUTES = {
  [ZahavDayType.REGULAR]: toMinutes(ZAHAV_REGULAR_CLOSE_TIME),
  [ZahavDayType.SHORT]: toMinutes(ZAHAV_SHORT_CLOSE_TIME)
};
const LOOK_AHEAD_DAYS = 14;

/** Zahav operating status at `now`, evaluated in Israel time regardless of the host timezone. */
export function getZahavAvailability(now: Date): PayAvailabilityDTO {
  const {isoDate, weekday, minutes} = getWallClockInTimeZone(now, BUSINESS_TIME_ZONE);
  const todayType = getBankDayType(isoDate, weekday);

  let firstOffset = 1;
  if (todayType !== ZahavDayType.CLOSED) {
    if (minutes >= OPEN_MINUTES && minutes < CLOSE_MINUTES[todayType]) return {isOpen: true};
    if (minutes < OPEN_MINUTES) firstOffset = 0;
  }

  const next = findNextOpening(isoDate, firstOffset);
  return {
    isOpen: false,
    opensAt: next ? openingInstant(next.isoDate).toISOString() : null,
    opensInDays: next?.offset ?? null,
    todayType
  };
}

/** Availability for a payin on `transferType` — only zahav is hours-restricted. */
export function getPayAvailability(params: {
  transferType: PayinTransferType | null;
  now: Date;
}): PayAvailabilityDTO {
  if (params.transferType !== TransferType.ZAHAV) return {isOpen: true};
  return getZahavAvailability(params.now);
}

function toMinutes(time: string): number {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function openingInstant(isoDate: string): Date {
  return wallClockToInstant(
    isoDate,
    Math.floor(OPEN_MINUTES / 60),
    OPEN_MINUTES % 60,
    BUSINESS_TIME_ZONE
  );
}

function findNextOpening(
  fromIsoDate: string,
  firstOffset: number
): {isoDate: string; offset: number} | null {
  for (let offset = firstOffset; offset <= LOOK_AHEAD_DAYS; offset++) {
    const isoDate = addDaysToIsoDate(fromIsoDate, offset);
    if (getBankDayType(isoDate) !== ZahavDayType.CLOSED) {
      return {isoDate, offset};
    }
  }
  return null;
}
